import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { MillosMark, MillosWordmark } from '../marketing/components/icons';
import { homepageStyles } from '../theme_landing_page_2';
import PasswordRequirements from './PasswordRequirements';
import { isPasswordValid } from '../passwordPolicy';

const Register: React.FC = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Create your account | MillOS';
    return () => { document.title = previousTitle; };
  }, []);
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid(password)) {
      setError('Your password does not meet all the requirements below');
      return;
    }

    if (!agreed) {
      setError('You must agree to the Terms and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      // Remembered across the redirects that follow sign-up: new accounts go to onboarding once verified
      sessionStorage.setItem('after_verify_next', '/onboarding');
      await register(email, password, name, agreed);
      navigate('/check-email', { state: { next: '/onboarding' } });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: palette.tokens.canvas,
      '& fieldset': { borderColor: palette.tokens.hairline },
      '&:hover fieldset': { borderColor: palette.tokens.mutedSoft },
      '&.Mui-focused fieldset': { borderColor: palette.tokens.primary, borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': {
      py: '14px',
      fontSize: '15px',
    },
    '& .MuiInputBase-input::placeholder': {
      color: palette.tokens.mutedSoft,
      opacity: 1,
    },
    // Browsers (Chrome especially) force their own blue/yellow tint on autofilled inputs,
    // overriding the canvas background above — the standard trick to override that back.
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${palette.tokens.canvas} inset`,
      WebkitTextFillColor: palette.tokens.ink,
      caretColor: palette.tokens.ink,
    },
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100svh', bgcolor: palette.tokens.canvas }}>
      {/* Left — form panel */}
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '0 0 52%' },
          boxSizing: 'border-box',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2, sm: 4, lg: 6 },
          py: 4,
          backgroundColor: palette.tokens.canvas,
          backgroundImage: `radial-gradient(ellipse at 15% 15%, ${alpha(palette.tokens.primary, 0.08)}, transparent 55%), radial-gradient(ellipse at 85% 85%, ${alpha(palette.tokens.accentTeal, 0.08)}, transparent 55%)`,
        }}
      >
        <Link component={RouterLink} to="/" aria-label="MillOS home" underline="none" sx={{ ...homepageStyles, display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 3, borderRadius: 2, '&:focus-visible': { outline: `2px solid ${palette.tokens.primary}`, outlineOffset: 6 } }}>
          <MillosMark size={48} />
          <MillosWordmark fontSize="1.8rem" />
        </Link>
        <Box sx={{ width: '100%', maxWidth: 440, boxSizing: 'border-box', p: { xs: 3, sm: 4 }, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '24px', bgcolor: palette.tokens.canvas, boxShadow: `0 16px 48px ${alpha(palette.tokens.ink, 0.06)}` }}>

        <Typography
          variant="displayMd"
          component="h1"
          sx={{ color: palette.tokens.ink, mb: 1, textAlign: 'center' }}
        >
          Create your account.
        </Typography>

        <Typography sx={{ color: palette.tokens.muted, fontSize: '14px', mb: 3, lineHeight: 1.6, textAlign: 'center' }}>
          Start your 7-day free trial. Add up to 3 certificates, no card needed.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Full Name */}
          <Typography component="label" htmlFor="register-name" sx={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: palette.tokens.muted, textTransform: 'uppercase', mb: 0.75 }}>
            Full Name
          </Typography>
          <TextField
            fullWidth
            id="register-name"
            autoComplete="name"
            placeholder="Sarah K."
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={loading}
            sx={{ ...fieldStyles, mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Work Email */}
          <Typography component="label" htmlFor="register-email" sx={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: palette.tokens.muted, textTransform: 'uppercase', mb: 0.75 }}>
            Work Email
          </Typography>
          <TextField
            fullWidth
            type="email"
            id="register-email"
            autoComplete="email"
            placeholder="you@portfolio.co"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            sx={{ ...fieldStyles, mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Password */}
          <Typography component="label" htmlFor="register-password" sx={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: palette.tokens.muted, textTransform: 'uppercase', mb: 0.75 }}>
            Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            id="register-password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
            inputProps={{ 'aria-describedby': 'password-requirements' }}
            sx={{ ...fieldStyles, mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: palette.tokens.mutedSoft, fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff sx={{ color: palette.tokens.mutedSoft }} /> : <Visibility sx={{ color: palette.tokens.mutedSoft }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordRequirements password={password} id="password-requirements" />

          {/* Terms checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                size="small"
                sx={{ color: palette.tokens.primary, '&.Mui-checked': { color: palette.tokens.primary } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: palette.tokens.body }}>
                I agree to the{' '}
                <Link href="#" underline="hover" sx={{ color: palette.tokens.primary }}>Terms</Link>
                {' '}and{' '}
                <Link component={RouterLink} to="/privacy" underline="hover" sx={{ color: palette.tokens.primary }}>Privacy Policy</Link>.
              </Typography>
            }
            sx={{ mb: 3, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0 } }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: '14px',
              borderRadius: '12px',
              backgroundColor: palette.tokens.primary,
              color: palette.tokens.onPrimary,
              fontSize: '15px',
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: 0,
              boxShadow: 'none',
              '&:hover': { backgroundColor: palette.tokens.primaryActive, boxShadow: 'none' },
              mb: 3,
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: palette.tokens.white }} /> : 'Start free trial →'}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', pt: 2.5, borderTop: `1px solid ${palette.tokens.hairline}` }}>
          <Typography variant="body2" sx={{ color: palette.tokens.muted, fontSize: '14px' }}>
            Already have an account?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{ color: palette.tokens.primary, textDecoration: 'none', fontWeight: 600 }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
        </Box>
        <Link component={RouterLink} to="/" variant="bodySm" sx={{ mt: 3, color: palette.tokens.muted, textUnderlineOffset: '4px' }}>Back to MillOS</Link>
      </Box>

      {/* Right — introduction panel */}
      <Box
        sx={{
          flex: 1,
          background: `linear-gradient(135deg, ${palette.tokens.surfaceDark} 0%, ${palette.tokens.surfaceDarkElevated} 50%, ${palette.tokens.surfaceDark} 100%)`,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          px: { md: 5, lg: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle radial glow */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '10%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(palette.tokens.accentOnDark, 0.2)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        {/* Status badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: alpha(palette.tokens.onDark, 0.08),
            border: `1px solid ${alpha(palette.tokens.onDark, 0.12)}`,
            borderRadius: '20px',
            px: 2,
            py: 0.75,
            mb: 6,
            width: 'fit-content',
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: palette.tokens.accentOnDark, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: palette.tokens.onDarkSoft, textTransform: 'uppercase' }}>
            Your property portfolio, connected
          </Typography>
        </Box>

        <Typography
          variant="displayLg"
          sx={{
            fontSize: { md: '2.6rem', lg: '3.4rem' },
            lineHeight: 1.2,
            color: palette.tokens.onDark,
            mb: 3,
          }}
        >
          Less property admin.<br />
          <Box component="span" sx={{ color: palette.tokens.accentOnDark }}>
            More time for you.
          </Box>
        </Typography>

        <Typography sx={{ fontSize: '16px', color: palette.tokens.onDarkSoft, lineHeight: 1.7, maxWidth: 400 }}>
          Keep your certificates, accounting and AI agents in one place. Start with your properties, then let MillOS help you stay on top of what needs attention.
        </Typography>
      </Box>
    </Box>
  );
};

export default Register;
